import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { camelize } from "humps";
import { createWrapper } from "main/features/AxiosWrapper";
import { Booking } from "main/types/booking";
import { ErrorResponse } from "main/types/errorResponse";
import {
  isPendingAction,
  isRejectedAction,
  isFulfilledAction,
  deepMerge,
} from "main/utils/sliceUtil";
import { API_BASE_PATH } from "main/constants/constant";
import { RootState } from "main/store";
import { HYDRATE } from "next-redux-wrapper";
import { Meta } from "../../types/meta";
import { HotelFacilityTypes } from "main/constants/hotelFacilityTypes";
import { BookingTranslationTypes } from "../../constants/bookingTranslationTypes";
import { HotelChildrenSettingAllCategories } from "../../constants/hotelChildrenSettingCategories";

const key = "booking";
const adapter = createEntityAdapter({
  selectId: (booking: Booking.Booking) => booking.code,
});

const initialStateValues: Booking.BookingState = {
  booking: null,
  error: null,
  loading: false,
  meta: null,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async * */
export const postBooking = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  { hotelId: string; param: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postBooking`, async (args, { rejectWithValue }) => {
  try {
    const { hotelId, param } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/bookings.json`,
      {
        booking: param,
      },
    );
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchBookings = createAsyncThunk<
  {
    bookings: Booking.Booking[];
    meta: Meta;
  },
  {
    hotelId: string;
    param?: string;
    headers: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchBookings`, async (args, { rejectWithValue }) => {
  try {
    const { hotelId, param, headers, isServerSide, locale } = args;
    const url = `${API_BASE_PATH}/v1/hotels/${hotelId}/bookings.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      bookings: result.data.bookings,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchBooking = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    id: string;
    hotelId?: string;
    token: string;
    headers: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchBooking`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, headers, isServerSide, locale, token } = args;
    const url = hotelId
      ? `${API_BASE_PATH}/v1/hotels/${hotelId}/bookings/${id}.json?token=${token}`
      : `${API_BASE_PATH}/v1/bookings/${id}.json?token=${token}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    // console.dir(result.data.booking, { depth: null });
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue({ ...err.data.errors, status: err.status });
  }
});

export const fetchBookingHotelOptions = createAsyncThunk<
  {
    code: string;
    hotelOptions: Booking.HotelOption[];
  },
  {
    id: string;
    hotelId: string;
    headers?: any;
    isServerSide?: boolean;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchBookingHotelOptions`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, headers, isServerSide, locale } = args;
    const result = await createWrapper({ isServerSide, locale }).get(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/bookings/${id}/hotel_options.json`,
      {
        headers,
      },
    );
    return {
      code: id,
      hotelOptions: result.data.hotelOptions,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postBookingOptions = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  { id: string; hotelId: string; param: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postBookingOptions`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, param } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/bookings/${id}/options.json`,
      {
        ...param,
      },
    );
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const cancelBooking = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    id: string;
    hotelId: string;
    token: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/cancelBooking`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, token } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/bookings/${id}/cancellations.json`,
      { token },
    );
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const cancelBookingRoom = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    id: string;
    hotelId: string;
    token: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/cancelBookingRoom`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, token } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/booking_rooms/${id}/cancellations.json`,
      { token },
    );
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const cancelBookingOption = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    id: number;
    hotelId: string;
    token: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/cancelBookingOption`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, token } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/booking_options/${id}/cancellations.json`,
      { token },
    );
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putBooking = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    id: string;
    token: string;
    hotelId: string;
    param: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putBooking`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, param, token } = args;
    const result = await createWrapper().put(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/bookings/${id}.json`,
      { booking: param, token },
    );
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const authBooking = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    hotelId: string;
    code: string;
    email: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/authBooking`, async (args, { rejectWithValue }) => {
  try {
    const { hotelId, code, email } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/bookings/auth.json`,
      { code, email },
    );
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const resendBookingMail = createAsyncThunk<
  {
    booking: Booking.Booking;
  },
  {
    id: string;
    token: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/resendBookingMail`, async (args, { rejectWithValue }) => {
  try {
    const { id, token } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/v1/bookings/${id}/resend_mails.json`,
      { token },
    );
    return {
      booking: result.data.booking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putBookingCreditCard = createAsyncThunk<
  {
    redirectUrl: string;
  },
  {
    id: string;
    token: string;
    creditCardToken: string;
    fromAdmin: boolean;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putBookingCreditCard`, async (args, { rejectWithValue }) => {
  try {
    const { id, token, creditCardToken, fromAdmin } = args;
    const result = await createWrapper().put(
      `${API_BASE_PATH}/v1/bookings/${id}/credit_card.json`,
      { token, credit_card_token: creditCardToken, from_admin: fromAdmin },
    );
    return {
      redirectUrl: result.data.redirectUrl,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice * */
export const bookingSlice = createSlice({
  name: key,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        const booking = deepMerge(state, action.payload.booking);
        return { ...state, ...booking };
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.bookings);
        state.meta = action.payload.meta;
      })
      .addCase(fetchBooking.fulfilled, (state, action) => {
        state.booking = action.payload.booking;
        adapter.addOne(state, action.payload.booking);
      })
      .addCase(resendBookingMail.fulfilled, (state, action) => {
        state.booking = action.payload.booking;
        adapter.addOne(state, action.payload.booking);
      })
      .addCase(postBooking.fulfilled, (state, action) => {
        state.booking = action.payload.booking;
        adapter.addOne(state, action.payload.booking);
      })
      .addCase(fetchBookingHotelOptions.fulfilled, (state, action) => {
        const { code, hotelOptions } = action.payload;
        adapter.updateOne(state, {
          id: code,
          changes: { hotelOptions: hotelOptions },
        });
        state.booking.hotelOptions = hotelOptions;
      })
      .addCase(postBookingOptions.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.booking);
        state.booking = action.payload.booking;
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        const { code, ...changes } = action.payload.booking;
        adapter.updateOne(state, { id: code, changes });
        state.booking = action.payload.booking;
      })
      .addCase(cancelBookingRoom.fulfilled, (state, action) => {
        const { code, ...changes } = action.payload.booking;
        adapter.updateOne(state, { id: code, changes });
        state.booking = action.payload.booking;
      })
      .addCase(cancelBookingOption.fulfilled, (state, action) => {
        const { code, ...changes } = action.payload.booking;
        adapter.updateOne(state, { id: code, changes });
        state.booking = action.payload.booking;
      })
      .addCase(putBooking.fulfilled, (state, action) => {
        const { code, ...changes } = action.payload.booking;
        adapter.updateOne(state, { id: code, changes });
        state.booking = action.payload.booking;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.error = action.payload?.error_messages;
        state.loading = false;
      });
  },
});

/** selector * */
const stateSelector = (state: RootState) => state[key];

export const { selectAll: bookingsSelector, selectById: bookingByIdSelector } =
  adapter.getSelectors<RootState>((state) => state.booking);

export const bookingSelector = createSelector(
  stateSelector,
  (state) => state.booking,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const metaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);

export const commonScriptSelector = createSelector(
  stateSelector,
  (state) => state.booking?.script,
);

export const commonNoscriptSelector = createSelector(
  stateSelector,
  (state) => state.booking?.noscript,
);

export const bookingScriptSelector = createSelector(
  stateSelector,
  (state) => state.booking?.bookingScript,
);

export const bookingNoscriptSelector = createSelector(
  stateSelector,
  (state) => state.booking?.bookingNoscript,
);

export const bookingTranslationTypeSelector = createSelector(
  stateSelector,
  (state) => {
    switch (state.booking?.hotelFacilityType) {
      case HotelFacilityTypes.activities:
        return BookingTranslationTypes.bookingActivity;
      case HotelFacilityTypes.camping:
        return BookingTranslationTypes.bookingCamp;
      default:
        return BookingTranslationTypes.booking;
    }
  },
);

export const convertBookingFormValuesToParams = (data: any) => {
  const params = { ...data };
  if (params.booking_options && params.booking_options.length > 0) {
    params.booking_options = params.booking_options.filter((bookingOption) => {
      return Number(bookingOption.quantity) > 0;
    });
  }
  if (params.registration) {
    delete params.registration;
  }
  return params;
};

export const checkinAvailableText = (from?: string, to?: string) => {
  const hasFromText = !!from;
  const hasToText = !!to;
  const fromText = hasFromText ? `${from} ` : "";
  const toText = hasToText && from !== to ? `${to}` : "";
  const separator = hasFromText || hasToText ? ` ~ ` : "";
  return `${fromText}${separator}${toText}`;
};

export const getDiscountText = (
  promotionDiscounts: any[],
  totalDiscountAmount: string,
  t: any,
) => {
  const promotionDiscount = promotionDiscounts?.[0];
  const promotionCount = promotionDiscounts?.length;
  if (!promotionDiscounts || promotionCount === 0) {
    return "";
  }
  return promotionCount > 1
    ? `${promotionDiscount.promotionName}(${t("component:common.otherCount", {
        count: promotionCount - 1,
      })}): -${totalDiscountAmount}`
    : `${promotionDiscount.promotionName}: -${totalDiscountAmount}`;
};

export const getEcommerceEventValues = (booking: Booking.Booking) => {
  const roomValues = booking.bookingRooms.map((room, index) => {
    return {
      item_id: `${room.hotelRoomId}-${room.hotelPlanId}`,
      item_brand: booking.hotelCode,
      item_category: room.hotelRoomId,
      price:
        index === 0
          ? room.totalPriceCents - booking.fixedTotalTargetedDiscountAmountCents
          : room.totalPriceCents,
      quantity: 1,
    };
  });
  const optionValues = booking.bookingOptions.map((option) => {
    return {
      item_id: `options-${option.id}`,
      item_brand: booking.hotelCode,
      item_category: `options-${option.id}`,
      price: option.priceCents,
      quantity: option.quantity,
    };
  });
  return {
    items: [...roomValues, ...optionValues],
    transaction_id: booking.code,
    currency: booking.totalPriceCurrency,
    value: booking.totalPriceCents,
  };
};

export const getNumberOfGuestsLabel = (isNonStay: boolean, t: any) => {
  return isNonStay
    ? t("label:hotel.numberOfDayUseGuests")
    : t("label:hotel.numberOfGuests");
};

export const getGuestNameLabel = (isNonStay: boolean, t: any) => {
  return isNonStay
    ? t("component:booking.dayUseGuestName")
    : t("component:booking.guestName");
};

export const getDateOfStayLabel = (isNonStay: boolean, t: any) => {
  return isNonStay ? t("label:hotel.dateOfUse") : t("label:hotel.dateOfStay");
};

export const bookingRoomTotalChildren = (bookingRoom: Booking.BookingRoom) => {
  const childrenKeys = Object.keys(HotelChildrenSettingAllCategories).map(
    (key) => {
      return HotelChildrenSettingAllCategories[key];
    },
  );
  return childrenKeys.reduce((acc: number, cur: string) => {
    const children = bookingRoom[camelize(cur)];
    return children ? acc + Number(children) : acc;
  }, 0);
};

export const redirectToBookingErrorProps = (errorCode: string) => {
  return {
    redirect: {
      destination: `/errors/booking_error?error_code=${errorCode}`,
      permanent: false,
    },
  };
};
